import { createContext, useContext, useState, useCallback, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Film } from "lucide-react";
import { resolveUrl, isUploadedVideo, getEmbedUrl } from "../../lib/media";
import { useScrollLock } from "../../lib/useScrollLock";

const ShowreelContext = createContext(null);
export const useShowreel = () => useContext(ShowreelContext);

const toEmbed = (url) => {
  if (!url || isUploadedVideo(url)) return null;
  const e = getEmbedUrl(url);
  return e && e !== url ? `${e}${e.includes("?") ? "&" : "?"}autoplay=1&rel=0` : null;
};

export const ShowreelProvider = ({ children }) => {
  const [state, setState] = useState({ open: false, url: "", title: "", cat: "", format: "auto" });
  const open = useCallback((url = "", title = "", cat = "", format = "auto") => setState({ open: true, url, title, cat, format }), []);
  const close = useCallback(() => setState((s) => ({ ...s, open: false })), []);
  useScrollLock(state.open);
  useEffect(() => {
    if (!state.open) return;
    const onKey = (e) => { if (e.key === "Escape") close(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [state.open, close]);

  const embed = toEmbed(state.url);
  const isFile = state.url && isUploadedVideo(state.url);
  const vertical = state.format === "9:16";
  const ratioCls = vertical ? "aspect-[9/16]" : "aspect-video";
  const maxWidth = vertical ? "min(100%, calc(78dvh * 9 / 16))" : "min(100%, calc(78dvh * 16 / 9))";

  return (
    <ShowreelContext.Provider value={{ open, close }}>
      {children}
      <AnimatePresence>
        {state.open && (
          <motion.div
            data-testid="showreel-modal"
            className="fixed inset-0 z-[10000] flex items-center justify-center p-4 md:p-10 bg-black/85 backdrop-blur-xl"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            onClick={close}
          >
            <button
              data-testid="showreel-close-btn"
              onClick={close}
              className="absolute top-6 right-6 md:top-8 md:right-10 w-12 h-12 rounded-full border border-white/20 flex items-center justify-center text-white/80 hover:border-electric hover:text-electric transition-colors z-10"
            >
              <X size={22} />
            </button>

            <motion.div
              className="w-full max-w-6xl flex flex-col max-h-[86dvh]"
              initial={{ scale: 0.92, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 10 }}
              transition={{ duration: 0.5, ease: [0.25, 1, 0.5, 1] }}
              onClick={(e) => e.stopPropagation()}
            >
              {(state.title || state.cat) && (
                <div className="mb-4 flex items-center gap-3 shrink-0">
                  {state.cat && <span className="text-[10px] tracking-[0.3em] uppercase text-electric">{state.cat}</span>}
                  {state.title && <span className="font-serif text-2xl text-white">{state.title}</span>}
                </div>
              )}

              <div className={`relative w-full ${ratioCls} rounded-lg overflow-hidden border border-white/10 bg-black mx-auto`} style={{ maxHeight: "78dvh", maxWidth }}>
                {embed ? (
                  <iframe src={embed} title={state.title || "Showreel"} className="w-full h-full" frameBorder="0" allow="autoplay; fullscreen; picture-in-picture" allowFullScreen />
                ) : isFile ? (
                  <video src={resolveUrl(state.url)} className="w-full h-full object-contain bg-black" autoPlay controls playsInline preload="auto" />
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center text-center px-6">
                    <div className="w-16 h-16 rounded-full border border-electric/50 flex items-center justify-center mb-6">
                      <Film size={24} className="text-electric" />
                    </div>
                    <div className="font-serif text-3xl md:text-4xl text-white mb-3">Bande-démo bientôt disponible</div>
                    <p className="text-white/50 max-w-md text-sm font-light">
                      La vidéo sera ajoutée ici prochainement. Contactez-nous pour découvrir nos réalisations en avant-première.
                    </p>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </ShowreelContext.Provider>
  );
};
